import { useDispatch } from 'react-redux'
import P from 'components/typography/Paragraph'
import SocialIcon from 'components/misc/SocialIcon'
import Link from 'components/misc/Link'
import { toggleModal } from 'store/ui/actions'
import styles from './styles/Footer.css'
import headerLvl from 'components/typography/styles/Header.css'

const VISUALIZATIONS_EXAMPLE =
  '/visualizations/global-analysis-of-genome-transcriptome-9L4dJr?dois[0]=10.1038%2Fmsb.2012.40&dois[1]=10.7554%2Felife.05068&focusedElement=10.7554%2Felife.05068'

const About = () => (
  <div className={styles.aboutContainer}>
    <img
      width='128px'
      height='41px'
      alt='scite logo'
      className={styles.logo}
      src={`${CONFIG.assetsBaseURL}/images/logo.svg`}
    />
    <P className={styles.aboutText}>
      Scite is an AI-powered research tool that helps researchers better discover and evaluate scientific literature
      through Smart Citations—a revolutionary system that shows whether articles support, contrast, or simply mention a
      given claim. Founded in 2018, and now part of Research Solutions, Scite has indexed over{' '}
      {CONFIG.defaultCitationsTotalText.split('b')[0]} billion citations and partnered with more than 30 major
      publishers to provide researchers with unparalleled access to scientific literature. With its Scite Assistant,
      Smart Citation Index, and advanced search capabilities, the platform addresses critical challenges such as
      information overload and research reproducibility. Trusted by two million active users worldwide, Scite is
      reshaping how researchers interact with scholarly content—building ethical, transparent AI tools that support
      rigorous, copyright-compliant research.
    </P>
    <Contact />
  </div>
)

const Contact = () => (
  <div className={styles.contact}>
    <div className={headerLvl.h6}>Contact Info</div>
    <div>
      <P className={styles.address}>customersupport@researchsolutions.com</P>
      <P className={styles.address}>10624 S. Eastern Ave., Ste. A-614</P>
      <P className={styles.address}>Henderson, NV 89052, USA</P>
    </div>
  </div>
)

const SocialIcons = () => (
  <div className={styles.iconsLayout}>
    <SocialIcon className={styles.icon} type='x' />
    <SocialIcon className={styles.icon} type='facebook' />
    <SocialIcon className={styles.icon} type='instagram' />
    <SocialIcon className={styles.icon} type='tiktok' />
    <SocialIcon className={styles.icon} type='linkedin' />
    <SocialIcon className={styles.icon} type='youtube' />
  </div>
)

const StaticLinks = () => {
  const dispatch = useDispatch()

  return (
    <div className={styles.staticContainer}>
      <div className={styles.staticLayout}>
        <P type='small' className={styles.staticLinks}>
          <Link
            className={styles.link}
            to='/blog'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Blog (opens in a new tab)'
          >
            Blog
          </Link>
          <Link className={styles.link} to='/terms'>
            Terms of Service
          </Link>
          <Link className={styles.link} to='/apiterms' aria-label='API Terms'>
            API Terms
          </Link>
          <Link className={styles.link} to='/policy'>
            Privacy Policy
          </Link>
          <Link className={styles.link} href='mailto:customersupport@researchsolutions.com'>
            Contact
          </Link>
          <Link className={styles.link} onClick={() => dispatch(toggleModal('cookie', true, 'generic'))}>
            Cookie Preferences
          </Link>
          <Link className={styles.link} onClick={() => dispatch(toggleModal('cookie', true, 'do_not_sell'))}>
            Do Not Sell or Share My Personal Information
          </Link>
        </P>
        <SocialIcons />
      </div>
    </div>
  )
}

const ProductLinks = () => (
  <div className={styles.productContainer}>
    <div>
      <div className={headerLvl.h6}>Product</div>
      <div className={styles.products}>
        <Link className={styles.link} to='/extension-install'>
          Browser Extension
        </Link>
        <Link className={styles.link} to='/assistant' aria-label='Assistant'>
          Assistant by scite
        </Link>
        <Link className={styles.link} to='/search/citations' aria-label='Citation Statement Search'>
          Citation Statement Search
        </Link>
        <Link className={styles.link} to='/?referenceCheck=true'>
          Reference Check
        </Link>
        <Link className={styles.link} to={VISUALIZATIONS_EXAMPLE}>
          Visualizations
        </Link>
        <Link className={styles.link} to='/create-dashboard' aria-label='Dashboards'>
          Dashboards
        </Link>
        <Link className={styles.link} to='/journals' aria-label='Explore Journals'>
          Explore Journals
        </Link>
        <Link className={styles.link} to='/affiliations'>
          Explore Organizations
        </Link>
        <Link className={styles.link} to='/institutions'>
          Explore Funders
        </Link>
        <Link className={styles.link} to='/badge' aria-label='Badge'>
          Embedding Badge
        </Link>
        <Link className={styles.link} to='/embed-citation-search' aria-label='Embedding Citation Search'>
          Embedding Citation Search
        </Link>
        <Link className={styles.link} to='/pricing'>
          Pricing
        </Link>
      </div>
    </div>
    <div>
      <div className={headerLvl.h6}>Resources</div>
      <div className={styles.resources}>
        <Link
          className={styles.link}
          to='/blog'
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Blog (opens in a new tab)'
        >
          Blog
        </Link>
        <Link
          className={styles.link}
          href='https://help.researchsolutions.com/hc/en-us/categories/31947397460116-Scite'
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Help & FAQ (opens in a new tab)'
        >
          Help & FAQ
        </Link>
        <Link className={styles.link} to='/accessibility-statement'>
          Accessibility Statement
        </Link>
        <Link className={styles.link} to='/apiterms' aria-label='API Terms'>
          API Terms
        </Link>
        <Link className={styles.link} to='/partners/academic-institutions'>
          For Universities & Governments
        </Link>
        <Link className={styles.link} to='/partners/researchers'>
          For Researchers
        </Link>
        <Link className={styles.link} to='/partners/publishers'>
          For Publishers
        </Link>
        <Link className={styles.link} to='/partners/pharmaceuticals-and-biotech'>
          For Corporate, Pharma & Enterprise
        </Link>
        <Link className={styles.link} to='/author-marketing'>
          Author Marketing
        </Link>
        <Link className={styles.link} to='/pricing?tab=organizations'>
          Get an organization trial or quote
        </Link>
        <Link className={styles.link} to='/data-and-services' aria-label='Data and Services'>
          scite Data & Services
        </Link>
      </div>
    </div>
    <div>
      <div className={headerLvl.h6}>About</div>
      <div className={styles.about}>
        <Link
          className={styles.link}
          to='/news-and-press'
          target='_blank'
          rel='noopener noreferrer'
          aria-label='News and Press (opens in a new tab)'
        >
          News & Press
        </Link>
        <Link className={styles.link} to='/jobs'>
          Careers
        </Link>
        <Link
          className={styles.link}
          href='https://direct.mit.edu/qss/article/2/3/882/102990/scite-A-smart-citation-index-that-displays-the'
        >
          Read our Paper
        </Link>
        <Link className={styles.link} to='/data-and-services' aria-label='Data and Services'>
          Coverage
        </Link>
      </div>
    </div>
  </div>
)

const ReCaptchaInfo = () => (
  <div className={styles.recaptchaInfo}>
    <P>
      This site is protected by reCAPTCHA and the Google{' '}
      <Link
        href='https://policies.google.com/privacy'
        underline
        target='_blank'
        rel='noopener noreferrer'
        aria-label='Privacy Policy (opens in a new tab)'
      >
        Privacy Policy
      </Link>{' '}
      and{' '}
      <Link
        href='https://policies.google.com/terms'
        underline
        target='_blank'
        rel='noopener noreferrer'
        aria-label='Terms of Service (opens in a new tab)'
      >
        Terms of Service
      </Link>{' '}
      apply.
    </P>
  </div>
)

const Copyright = () => (
  <div className={styles.copyrightContainer}>
    <div className={styles.copyrightLayout}>
      <P type='small' className={styles.copyright}>
        Copyright © {new Date().getFullYear()} Scite LLC. All rights reserved.
      </P>
      <P type='small' className={styles.madeWithLove}>
        Made with 💙 for researchers
      </P>

      <div className={styles.rsssFamily}>
        <P type='small' className={styles.rsssBlurb}>
          Part of the Research Solutions Family.
        </P>
        <img
          alt='Research Solutions Logo'
          src={`${CONFIG.assetsBaseURL}/images/logo-rs-black-new.png`}
          className={styles.rsssLogo}
        />
      </div>
    </div>
  </div>
)

export const Footer = () => (
  <div className={styles.layout}>
    <div className={styles.mainLayout}>
      <About />
      <ProductLinks />
    </div>
    <ReCaptchaInfo />
    <StaticLinks />
    <Copyright />
  </div>
)

export default Footer
